import { Boxes, Database, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { navigateToArchitectureProject, navigateToDashboard, navigateToProject } from '../App';
import AppHeader from '../components/workspace/AppHeader';
import { explanationSummary } from '../components/workspace/Markdown';
import NameDialog from '../components/workspace/NameDialog';
import { useArchitectureProjectsStore } from '../store/useArchitectureProjectsStore';
import { useProjectsStore } from '../store/useProjectsStore';
import { useWorkspaceStore, type DiagramKind } from '../store/useWorkspaceStore';

function relativeTime(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export default function WorkspaceProjectPage({ orgId, projectId }: { orgId: string; projectId: string }) {
  const org = useWorkspaceStore((s) => s.organizations.find((o) => o.id === orgId));
  const project = useWorkspaceStore((s) => s.projects.find((p) => p.id === projectId && p.orgId === orgId));
  const can = useWorkspaceStore((s) => s.can);
  const createDiagram = useWorkspaceStore((s) => s.createDiagram);
  const removeDiagram = useWorkspaceStore((s) => s.removeDiagram);
  const erProjects = useProjectsStore((s) => s.projects);
  const archProjects = useArchitectureProjectsStore((s) => s.projects);
  const [newKind, setNewKind] = useState<DiagramKind | null>(null);
  const [seedSample, setSeedSample] = useState(false);

  if (!org || !project) {
    return (
      <div className="page-bg flex flex-col items-center justify-center gap-3 dark:text-gray-200">
        <p>Project not found.</p>
        <button onClick={navigateToDashboard} className="btn-primary">
          Back to organizations
        </button>
      </div>
    );
  }

  const erDiagrams = erProjects.filter((d) => project.erIds.includes(d.id));
  const archDiagrams = archProjects.filter((d) => project.archIds.includes(d.id));

  const canCreate = can(orgId, 'diagrams.create');
  const canDelete = can(orgId, 'diagrams.delete');

  async function handleDelete(kind: DiagramKind, id: string, name: string) {
    if (!confirm(`Delete diagram "${name}"? This cannot be undone.`)) return;
    try {
      await removeDiagram(projectId, kind, id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete the diagram');
    }
  }

  function section(
    kind: DiagramKind,
    title: string,
    Icon: typeof Database,
    tileClass: string,
    items: { id: string; name: string; meta: string; updatedAt: string; summary: string }[],
  ) {
    return (
      <section className="mb-12">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-3 text-lg font-semibold tracking-tight dark:text-gray-100">
            <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${tileClass}`}>
              <Icon className="h-[18px] w-[18px]" />
            </span>
            {title}
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-white/10 dark:text-gray-400">{items.length}</span>
          </h2>
          {canCreate && (
            <button onClick={() => setNewKind(kind)} className="btn-primary !py-1.5">
              <Plus className="h-4 w-4" /> New
            </button>
          )}
        </div>
        {items.length === 0 && !canCreate ? (
          <div className="rounded-2xl border-2 border-dashed border-gray-300 bg-white/50 p-10 text-center text-sm text-gray-500 dark:border-white/15 dark:bg-white/5 dark:text-gray-400">
            Nothing here yet.
          </div>
        ) : items.length === 0 ? (
          <button
            onClick={() => setNewKind(kind)}
            className="flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 bg-white/50 p-10 text-sm text-gray-500 transition hover:border-sky-400 hover:bg-sky-50/50 hover:text-sky-600 dark:border-white/15 dark:bg-white/5 dark:text-gray-400 dark:hover:border-sky-500/60"
          >
            <Plus className="h-5 w-5" />
            Create your first {kind === 'er' ? 'ER' : 'architecture'} diagram
          </button>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((d) => (
              <div
                key={d.id}
                onClick={() => (kind === 'er' ? navigateToProject(d.id) : navigateToArchitectureProject(d.id))}
                className="surface-hover group relative p-5"
              >
                {canDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(kind, d.id, d.name);
                    }}
                    className="absolute right-3 top-3 rounded-lg p-1.5 text-gray-400 opacity-0 transition hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                    title="Delete diagram"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                <div className="truncate pr-6 text-base font-semibold dark:text-gray-100">{d.name}</div>
                <p className="mb-4 mt-1 line-clamp-2 min-h-[2.5rem] text-sm text-gray-500 dark:text-gray-400">
                  {d.summary || <span className="italic text-gray-300 dark:text-gray-600">No explanation yet</span>}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium dark:bg-white/10">{d.meta}</span>
                  <span>{relativeTime(d.updatedAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  return (
    <div className="page-bg">
      <AppHeader crumbs={[{ label: org.name, href: `#/org/${org.id}` }, { label: project.name }]} />
      <main className="mx-auto max-w-6xl px-6 pb-16 pt-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight dark:text-white">{project.name}</h1>
          <p className="mt-2 text-gray-500 dark:text-gray-400">
            {org.name} · {erDiagrams.length + archDiagrams.length} diagrams
          </p>
        </div>
        {section(
          'er',
          'ER Diagrams',
          Database,
          'bg-sky-50 text-sky-600 ring-1 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30',
          erDiagrams.map((d) => ({ id: d.id, name: d.name, meta: `${d.schema.tables.length} tables`, updatedAt: d.updatedAt, summary: explanationSummary(d.documentation) })),
        )}
        {section(
          'architecture',
          'Architecture Diagrams',
          Boxes,
          'bg-violet-50 text-violet-600 ring-1 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/30',
          archDiagrams.map((d) => ({ id: d.id, name: d.name, meta: `${d.diagram.nodes.length} nodes`, updatedAt: d.updatedAt, summary: explanationSummary(d.documentation) })),
        )}
      </main>

      {newKind && canCreate && (
        <NameDialog
          title={newKind === 'er' ? 'New ER Diagram' : 'New Architecture Diagram'}
          placeholder={newKind === 'er' ? 'e.g. Core Schema' : 'e.g. Production Infrastructure'}
          onCancel={() => setNewKind(null)}
          onConfirm={async (name) => {
            const id = await createDiagram(projectId, newKind, name, newKind === 'er' && seedSample);
            setNewKind(null);
            if (newKind === 'er') navigateToProject(id);
            else navigateToArchitectureProject(id);
          }}
        >
          {newKind === 'er' && (
            <label className="mb-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={seedSample} onChange={(e) => setSeedSample(e.target.checked)} />
              Start from example schema
            </label>
          )}
        </NameDialog>
      )}
    </div>
  );
}
